import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { logQuietError } from "@/lib/quiet-error";

const AuthContext = createContext(null);

/**
 * Cookie-only auth context (Phase 10c-E security finalization).
 *
 * Auth flow:
 *   1. /auth/login + /auth/register set an httpOnly `auth_token` cookie server-side.
 *   2. Every API request automatically attaches that cookie (withCredentials: true in api.js).
 *   3. /auth/me uses the cookie to resolve the current user — no token ever touches JS.
 *   4. /auth/logout clears the cookie server-side.
 *
 * The JWT is NEVER written to localStorage. We still clear any *legacy* token on
 * logout so older browser sessions that pre-date the cookie migration get cleaned
 * up the first time the user signs out.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // 401 on initial mount is the EXPECTED state for anonymous visitors —
      // don't pollute console with noise. Anything else is logged for ops.
      if (e?.response?.status !== 401) {
        logQuietError("auth.check", e);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Cookie set server-side — DO NOT touch localStorage.
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // Cookie set server-side — DO NOT touch localStorage.
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      logQuietError("auth.logout", e);
    }
    // Belt-and-suspenders: also clear any legacy token left by pre-migration sessions.
    try { localStorage.removeItem("teebee_token"); } catch { /* ignore */ }
    setUser(null);
  };

  const setSessionFromGoogle = (_token, nextUser) => {
    // Google OAuth callback path: server already set the session_token cookie. We just
    // hydrate the user object in React state — the cookie does the rest.
    setUser(nextUser);
  };

  const value = useMemo(
    () => ({ user, loading, login, register, logout, checkAuth, setSessionFromGoogle, setUser }),
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
